import { defineStore } from 'pinia'
import { ref, computed, type Ref } from 'vue'
import { useClaudeFleetService, activityFromLoopName, BOOKKEEPING_ACTIVITY, isRunLive } from '@/services/claude-fleet'
import { isFoldedBookkeepingRun, isRunMine, latestStep, recentFailuresFromPage, runMatchesFilters, sinceTime } from '@/services/claude-fleet/claude-fleet.runs'
import { isMyRun, type MineSets } from '@/services/my-work/only-mine.utils'
import type { FleetRunFilters } from '@/services/claude-fleet/claude-fleet.runs'
import type {
  FleetAuditEntry,
  FleetBrowserAutomation,
  FleetBrowserLiveView,
  FleetBrowserTakeover,
  FleetConfigRecord,
  FleetConfigResponse,
  FleetLoop,
  FleetLoopState,
  FleetRun,
  FleetRunDetail,
  FleetRunGradeValue,
  FleetSettings,
  FleetStartRunRequest,
  FleetStartRunResult,
  FleetSummary
} from '@/services/claude-fleet'
import { useAuthStore } from '@/stores/auth.store'
import { useFleetRunsSkipped } from '@/composables/useFleetRunsSkipped'
import { getErrorMessage, getErrorStatus, isApiError } from '@/utils/error-handler'

export const FLEET_RUNS_PAGE_SIZE = 25
/** The most failed runs the Overview reads for its last-24-hours count. */
export const FLEET_RECENT_FAILURES_LIMIT = 100

export type FleetSection = 'summary' | 'loops' | 'runs' | 'audit' | 'config'

export const FLEET_CONFIG_READ_ONLY_REASON = 'Only fleet operators can change these settings.'

/** Who may act comes with the settings, so while they cannot be read the page acts as for a non-operator. */
export const FLEET_CONFIG_UNREAD_REASON = 'The fleet settings could not be read, so actions are off until they load.'

/** Statuses the fleet uses to refuse a setting it will not save; the message names the setting. */
const CONFIG_VALIDATION_STATUSES = [400, 409, 422]

/** The fleet's answer to a give-back sent before the run's pause landed; the same click works shortly. */
const BROWSER_STILL_PAUSING_STATUS = 409

/** The fleet's 409 text for a browser session that has ended, unlike its 409 for a run still pausing. */
const BROWSER_SESSION_ENDED_TEXT = 'browser session has ended'

/** The line a run's log gets when its Claude session starts, after the runner has reset its inbox. */
const RESUMED_SESSION_STARTED = '▶ session started'
const RESUMED_SESSION_POLL_MS = 2000
/** Covers the runner's setup before a resumed session starts (credentials, plugins, browser re-attach). */
const RESUMED_SESSION_WAIT_MS = 180_000

const noRunFilters = (): FleetRunFilters => ({ outcome: null, trigger: null, since: null, search: null, mine: false, machine: null, graded: null, bookkeeping: false })

const noFailedSections = (): Record<FleetSection, boolean> => ({
  summary: false,
  loops: false,
  runs: false,
  audit: false,
  config: false
})

/** The overview reads besides the summary and runs, which every overview read makes. */
export type FleetOverviewSection = 'loops' | 'failedRuns'
const ALL_OVERVIEW_SECTIONS: readonly FleetOverviewSection[] = ['loops', 'failedRuns']

export const useClaudeFleetStore = defineStore('adx-claude-fleet', () => {
  const fleetService = useClaudeFleetService()
  const authStore = useAuthStore()

  const summary = ref<FleetSummary | null>(null)
  const summaryLoading = ref(false)

  const loops = ref<FleetLoop[]>([])
  const loopsLoading = ref(false)
  const loopsUpdating = ref<Record<string, boolean>>({})

  const runs = ref<FleetRun[]>([])
  /** Every running or paused run, laptops' own-inbox runs included, from the first page's reply; null from a fleet service that sends none. */
  const fleetRunningRuns = ref<FleetRun[] | null>(null)
  const runsLoading = ref(false)
  const activityFilter = ref<string | null>(null)
  const runFilters = ref<FleetRunFilters>(noRunFilters())
  /** The newest step of each running run's live log, for the runs pinned above the list. */
  const liveSteps = ref<Record<string, string | null>>({})
  /** The `before` cursor of each page the admin has paged through; the last one is the page shown. */
  const pageCursors = ref<(string | undefined)[]>([undefined])
  const nextCursor = ref<string | undefined>(undefined)
  /** The last 24 hours' failed runs, read apart from the Runs tab's filters and page, for the Overview. */
  const recentFailedRuns = ref<FleetRun[]>([])
  /** True when more failed in that time than the read returned. */
  const recentFailedMore = ref(false)
  /** True when the last read of those failed runs failed, so the Overview cannot say the fleet is healthy. */
  const recentFailedUnread = ref(false)

  const selectedRun = ref<FleetRunDetail | null>(null)
  const selectedRunLoading = ref(false)
  const runCommandPending = ref(false)
  /** Whether this person asked to be told when each run finishes, from the fleet's last answer. */
  const notifyRequested = ref<Record<string, boolean>>({})

  /** Who drives the selected run's browser, from the last live view or control the fleet answered. */
  const browserAutomation = ref<FleetBrowserAutomation | null>(null)
  const browserControlPending = ref(false)
  /** The operator who has the selected run's browser, from the last live view the fleet answered. */
  const browserTakenBy = ref<FleetBrowserTakeover | null>(null)
  /** The fleet's "still pausing" answer to a give-back, shown in the Live browser panel. */
  const browserControlNotice = ref<string | null>(null)

  const audit = ref<FleetAuditEntry[]>([])
  const auditLoading = ref(false)

  const config = ref<FleetConfigResponse['config']>({})
  const ceilings = ref<FleetConfigResponse['ceilings']>({})
  /** The fleet's own names and explanations, keyed by activity and by runner kind. */
  const describe = ref<NonNullable<FleetConfigResponse['describe']>>({})
  const kinds = ref<NonNullable<FleetConfigResponse['kinds']>>({})
  const configLoading = ref(false)
  const configSaving = ref<Record<string, boolean>>({})
  /** The fleet's refusal of each activity's last save, shown on that activity's card. */
  const configErrors = ref<Record<string, string | null>>({})
  /** Set when a save comes back 403: the admin is not a fleet operator, so every card turns read-only. */
  const configReadOnlyReason = ref<string | null>(null)

  const error = ref<string | null>(null)
  /** Set when the fleet refuses a read with 403: this admin is not fleet staff, so the page shows one
   *  access state and stops asking. Holds the fleet's own reason when it gave one. */
  const accessDenied = ref(false)
  const accessDeniedText = ref<string | null>(null)
  /** Set when a read got no HTTP answer at all, so the page offers one retry instead of a panel per tab. */
  const unreachable = ref(false)
  /** Sections whose last load failed, so each can show an error state with a retry instead of looking empty. */
  const failedSections = ref<Record<FleetSection, boolean>>(noFailedSections())
  /** What each failed section's error panel says: a server error, a refusal or no answer at all. */
  const sectionFailureTexts = ref<Partial<Record<FleetSection, string>>>({})

  const isConfigured = computed(() => Boolean(import.meta.env.VITE_FLEET_API_BASE_URL))
  const hasError = computed(() => error.value !== null)
  const isLoading = computed(
    () =>
      summaryLoading.value || loopsLoading.value || runsLoading.value || auditLoading.value || configLoading.value
  )
  const isConfigReadOnly = computed(() => configReadOnlyReason.value !== null)
  /** Whether this person may act on the fleet (start, switch, say, pause, resume, save). The fleet
   *  enforces it; the page only disables what it would refuse. False once the fleet says no, or while
   *  the settings that say it cannot be read. */
  const canAct = computed(() => !isConfigReadOnly.value)
  /** Set while actions are off only because the settings could not be read, so a tooltip says that. */
  const configUnreadReason = computed(() =>
    configReadOnlyReason.value === FLEET_CONFIG_UNREAD_REASON ? FLEET_CONFIG_UNREAD_REASON : null
  )
  /** Who may act on any laptop runner, from the fleet settings. */
  const operators = computed(() => {
    const listed = config.value.fleet?.settings.operators
    return Array.isArray(listed) ? listed : []
  })
  const hasOlderRuns = computed(() => Boolean(nextCursor.value))
  const hasNewerRuns = computed(() => pageCursors.value.length > 1)
  const runsPageNumber = computed(() => pageCursors.value.length)
  /** What My work says is the viewer's, so Only mine also keeps runs on their tickets and PRs; null until read. */
  const mineSets = ref<MineSets | null>(null)

  function setMineSets(sets: MineSets | null) {
    mineSets.value = sets
  }

  const isViewersRun = (run: FleetRun): boolean => {
    const email = authStore.user?.email
    return Boolean(email && isRunMine(run, email)) || Boolean(mineSets.value && isMyRun(run, mineSets.value))
  }

  /** Bookkeeping runs every few minutes, so the unfiltered list leaves it out unless asked to show it. */
  const bookkeepingHidden = computed(() => !runFilters.value.bookkeeping && !activityFilter.value)

  /**
   * What is running now, the list "Running now" and every running count read. The fleet's own list ignores the
   * page's filters and pages; without it, the running runs on the page shown stand in.
   */
  const runningRuns = computed(() => {
    if (!fleetRunningRuns.value) return visibleRuns.value.filter(run => isRunLive(run.state))
    return fleetRunningRuns.value.filter(run => !(bookkeepingHidden.value && isFoldedBookkeepingRun(run)))
  })

  const filteredRuns = computed(() => {
    const { mine, ...filters } = runFilters.value
    return runs.value.filter(
      run =>
        runMatchesFilters(run, filters, Date.now()) &&
        (!mine || isViewersRun(run)) &&
        !(bookkeepingHidden.value && isFoldedBookkeepingRun(run))
    )
  })

  /** Runs that found nothing to do are left out unless the viewer shows them or filters on Skipped. */
  const { showSkipped, setShowSkipped, skippedCount, hiddenSkippedCount, visibleRuns } = useFleetRunsSkipped(
    filteredRuns,
    computed(() => runFilters.value.outcome)
  )

  /** Every activity the fleet is known to run: this month's runs plus each loop's activity. */
  const knownActivities = computed(() => {
    const activities = new Set<string>(Object.keys(summary.value?.byActivity ?? {}))
    loops.value.forEach(loop => activities.add(activityFromLoopName(loop.name)))
    runs.value.forEach(run => activities.add(run.activity))
    return [...activities].sort()
  })

  /**
   * Records why a read failed. A 403 is the page-level access state rather than an error toast, and a
   * failure with no HTTP status is the page-level unreachable state. Returns true when it was a 403.
   */
  function noteReadFailure(failure: unknown): boolean {
    const status = getErrorStatus(failure)
    if (status === 403) {
      accessDenied.value = true
      accessDeniedText.value = fleetOwnErrorText(failure)
      return true
    }
    if (status === undefined) unreachable.value = true
    return false
  }

  /**
   * Loads one section, recording a failure on it so the view can offer a retry instead of an empty table.
   * A `background` load is the page's auto-refresh: no spinner, and a failure keeps what is shown, though
   * a refusal still turns into the page's access state so the refresh stops. Returns whether the read worked.
   */
  async function loadSection(section: FleetSection, loading: Ref<boolean>, load: () => Promise<void>, failure: string, { background = false } = {}): Promise<boolean> {
    if (accessDenied.value) return false
    if (background) {
      try {
        await load()
        // A section that failed earlier has recovered, so its error panel gives way to the fresh data.
        failedSections.value[section] = false
        unreachable.value = false
        return true
      } catch (e) {
        // The last good answer stays on screen; the next refresh or a manual one tries again.
        noteReadFailure(e)
        return false
      }
    }
    loading.value = true
    error.value = null
    failedSections.value[section] = false
    try {
      await load()
      unreachable.value = false
      return true
    } catch (e) {
      failedSections.value[section] = true
      sectionFailureTexts.value[section] = sectionFailureText(e)
      if (!noteReadFailure(e)) error.value = fleetErrorText(e, failure)
      return false
    } finally {
      loading.value = false
    }
  }

  async function fetchSummary({ background = false } = {}) {
    return loadSection('summary', summaryLoading, async () => {
      summary.value = await fleetService.getSummary()
    }, 'Failed to load the fleet summary', { background })
  }

  async function fetchLoops({ background = false } = {}) {
    return loadSection('loops', loopsLoading, async () => {
      loops.value = await fleetService.listLoops()
    }, 'Failed to load the fleet loops', { background })
  }

  async function fetchAudit() {
    await loadSection('audit', auditLoading, async () => {
      audit.value = await fleetService.listAudit()
    }, 'Failed to load the fleet audit log')
  }


  // One settings read at a time: the page and the Settings tab both ask on mount, and a second request
  // would only repeat the first.
  let configInFlight: Promise<void> | null = null

  async function fetchConfig() {
    if (configInFlight) return configInFlight
    configInFlight = readConfig().finally(() => {
      configInFlight = null
    })
    return configInFlight
  }

  async function readConfig() {
    await loadSection('config', configLoading, async () => {
      const response = await fleetService.getConfig()
      config.value = response.config
      ceilings.value = response.ceilings
      describe.value = response.describe ?? {}
      kinds.value = response.kinds ?? {}
      // The fleet says up front whether this person may edit, so the inputs start disabled rather
      // than only after a refused save.
      if (response.canEdit === false) configReadOnlyReason.value = FLEET_CONFIG_READ_ONLY_REASON
      else if (response.canEdit === true || configReadOnlyReason.value === FLEET_CONFIG_UNREAD_REASON) {
        configReadOnlyReason.value = null
      }
    }, 'Failed to load the fleet settings')
    if (failedSections.value.config && configReadOnlyReason.value === null) {
      configReadOnlyReason.value = FLEET_CONFIG_UNREAD_REASON
    }
  }

  /**
   * Saves the changed settings of one activity. A refusal stays on that activity's card — a 403 turns
   * every card read-only, a validation error sits under the card — so neither throws; anything else
   * goes to the page's error toast. Resolves true when the fleet saved the change.
   */
  async function saveConfig(activity: string, settings: FleetSettings): Promise<boolean> {
    configSaving.value = { ...configSaving.value, [activity]: true }
    configErrors.value = { ...configErrors.value, [activity]: null }
    error.value = null
    try {
      const saved = await fleetService.updateConfig(activity, settings)
      config.value = { ...config.value, [activity]: mergeSavedRecord(config.value[activity], saved) }
      return true
    } catch (e) {
      recordConfigFailure(activity, e)
      return false
    } finally {
      configSaving.value = { ...configSaving.value, [activity]: false }
    }
  }

  function recordConfigFailure(activity: string, failure: unknown) {
    const status = getErrorStatus(failure)
    if (status === 403) {
      configReadOnlyReason.value = fleetOwnErrorText(failure) ?? FLEET_CONFIG_READ_ONLY_REASON
      return
    }
    const message = fleetErrorText(failure, 'Failed to save the settings')
    if (status !== undefined && CONFIG_VALIDATION_STATUSES.includes(status)) {
      configErrors.value = { ...configErrors.value, [activity]: message }
      return
    }
    error.value = message
  }

  // Id of the latest runs fetch, so a slow reply for an earlier page or filter cannot overwrite a newer one.
  let activeRunsRequestId = 0

  /**
   * `background` is the auto-refresh: no spinner, and a failure keeps the list shown without a toast, though a
   * refusal still turns into the page's access state. Returns whether this read's answer is the one shown.
   */
  async function fetchRuns({ background = false } = {}): Promise<boolean> {
    if (accessDenied.value) return false
    const requestId = ++activeRunsRequestId
    if (!background) {
      runsLoading.value = true
      error.value = null
      failedSections.value.runs = false
    }
    try {
      const query = runsQuery()
      const [page, firstPageRunning] = await Promise.all([
        fleetService.listRuns(query),
        // The fleet sends `running` only on the first page, so a later page reads that page's list as well.
        query.before && fleetRunningRuns.value ? readFirstPageRunning() : null
      ])
      if (requestId !== activeRunsRequestId) return false
      runs.value = page.runs
      nextCursor.value = page.next
      const running = page.running ?? firstPageRunning
      if (running) fleetRunningRuns.value = running
      failedSections.value.runs = false
      unreachable.value = false
      return true
    } catch (e) {
      if (requestId !== activeRunsRequestId) return false
      if (background) {
        noteReadFailure(e)
        return false
      }
      failedSections.value.runs = true
      sectionFailureTexts.value.runs = sectionFailureText(e)
      if (!noteReadFailure(e)) error.value = fleetErrorText(e, 'Failed to load fleet runs')
      return false
    } finally {
      if (requestId === activeRunsRequestId) runsLoading.value = false
    }
  }

  /** The fleet's running list from the first page, one run wide; null when it cannot be read, so the last list stays. */
  async function readFirstPageRunning(): Promise<FleetRun[] | null> {
    try {
      return (await fleetService.listRuns({ limit: 1 })).running ?? null
    } catch {
      return null
    }
  }

  function runsQuery() {
    const { outcome, trigger, since, search, machine, graded } = runFilters.value
    return {
      activity: activityFilter.value ?? undefined,
      limit: FLEET_RUNS_PAGE_SIZE,
      before: pageCursors.value[pageCursors.value.length - 1],
      ...(outcome ? { outcome } : {}),
      ...(search ? { q: search } : {}),
      ...(trigger ? { trigger } : {}),
      ...(since ? { since: sinceTime(since, Date.now()) } : {}),
      ...(machine ? { runner: machine } : {}),
      ...(graded ? { graded } : {}),
      ...(bookkeepingHidden.value ? { hide: BOOKKEEPING_ACTIVITY } : {})
    }
  }

  // Id of the latest live-step read, so a slow reply cannot overwrite a newer one.
  let activeLiveStepsRequestId = 0

  /** Reads each running run's live log for its newest step; a run that cannot be read keeps its last one. */
  async function fetchLiveSteps(runIds: string[]) {
    const requestId = ++activeLiveStepsRequestId
    const steps = await Promise.all(
      runIds.map(async runId => {
        try {
          return [runId, latestStep((await fleetService.getRun(runId)).live?.tail)] as const
        } catch {
          return [runId, liveSteps.value[runId] ?? null] as const
        }
      })
    )
    if (requestId !== activeLiveStepsRequestId) return
    liveSteps.value = Object.fromEntries(steps)
  }

  async function fetchOlderRuns() {
    if (!nextCursor.value) return
    pageCursors.value = [...pageCursors.value, nextCursor.value]
    await fetchRuns()
  }

  async function fetchNewerRuns() {
    if (pageCursors.value.length <= 1) return
    pageCursors.value = pageCursors.value.slice(0, -1)
    await fetchRuns()
  }

  function setActivityFilter(activity: string | null) {
    activityFilter.value = activity
    resetRunsPaging()
  }

  function setRunFilters(filters: Partial<FleetRunFilters>) {
    runFilters.value = { ...runFilters.value, ...filters }
    resetRunsPaging()
  }

  function resetRunsPaging() {
    pageCursors.value = [undefined]
    nextCursor.value = undefined
  }

  function clearFilters() {
    activityFilter.value = null
    runFilters.value = noRunFilters()
    resetRunsPaging()
  }


  /**
   * A failed read keeps the last list; a foreground one also marks it unread, while a failed background
   * refresh leaves what the Overview shows. The Runs tab reports its own errors.
   */
  async function fetchRecentFailures({ background = false } = {}) {
    if (accessDenied.value) return
    try {
      const page = await fleetService.listRuns({
        outcome: 'error',
        since: sinceTime('24h', Date.now()),
        limit: FLEET_RECENT_FAILURES_LIMIT
      })
      recentFailedRuns.value = page.runs
      recentFailedMore.value = Boolean(page.next)
      recentFailedUnread.value = false
    } catch {
      if (!background) recentFailedUnread.value = true
    }
  }

  /** Whether the runs list is the newest page with no filter, so it can stand in for other reads. */
  function isFirstUnfilteredRunsPage(): boolean {
    const { outcome, trigger, since, search, machine, graded } = runFilters.value
    return pageCursors.value.length === 1 && !activityFilter.value && !outcome && !trigger && !since && !search && !machine && !graded
  }

  /**
   * The runs list, then the last day's failures from it when it covers the day, else from the fleet unless
   * `failedRuns` is left out of `sections` (the page reads them less often); true when the runs list was read.
   */
  async function fetchRunsAndRecentFailures({ background = false, sections = ALL_OVERVIEW_SECTIONS } = {}): Promise<boolean> {
    const runsRead = await fetchRuns({ background })
    const fromPage = runsRead
      ? recentFailuresFromPage(runs.value, { isFirstUnfilteredPage: isFirstUnfilteredRunsPage(), hasOlder: Boolean(nextCursor.value) }, Date.now())
      : null
    if (fromPage) {
      recentFailedRuns.value = fromPage
      recentFailedMore.value = false
      recentFailedUnread.value = false
    } else if (sections.includes('failedRuns')) {
      await fetchRecentFailures({ background })
    }
    return runsRead
  }

  /** When the page's data was last read in full, for its "updated N seconds ago" line. */
  const lastRefreshedAt = ref<number | null>(null)

  /**
   * Moves "updated N ago" only when every section read succeeded, so it never sits over an answer that
   * failed. `sections` names what to read; the page's auto-refresh leaves out what is not due yet.
   */
  async function fetchOverview({ background = false, sections = ALL_OVERVIEW_SECTIONS } = {}) {
    const reads = await Promise.all([
      fetchSummary({ background }),
      sections.includes('loops') ? fetchLoops({ background }) : true,
      fetchRunsAndRecentFailures({ background, sections })
    ])
    if (reads.every(Boolean) && !accessDenied.value) lastRefreshedAt.value = Date.now()
  }

  // Id of the latest single-run fetch; a poll that returns after the drawer moved on is dropped.
  let activeRunRequestId = 0

  /** `background` refreshes an open drawer without blanking it, as the live tail does every few seconds. */
  async function fetchRun(runId: string, { background = false } = {}) {
    const requestId = ++activeRunRequestId
    if (!background) {
      selectedRun.value = null
      selectedRunLoading.value = true
    }
    error.value = null
    try {
      const run = await fleetService.getRun(runId)
      if (requestId !== activeRunRequestId) return
      selectedRun.value = run
      runs.value = runs.value.map(listed => (listed.runId === run.runId ? { ...listed, ...stripLive(run) } : listed))
    } catch (e) {
      if (requestId !== activeRunRequestId) return
      error.value = fleetErrorText(e, 'Failed to load the run')
    } finally {
      if (requestId === activeRunRequestId) selectedRunLoading.value = false
    }
  }

  function clearSelectedRun() {
    activeRunRequestId++
    selectedRun.value = null
    selectedRunLoading.value = false
    clearBrowserState()
  }

  /**
   * A fresh live-view URL for the run's browser. It is returned, never kept in the store, because it
   * is a bearer capability for the session; the panel holds it only while it connects. Re-throws so
   * the panel can show an ended session (404, 409) or a failure in place.
   */
  async function fetchBrowserLiveView(runId: string): Promise<FleetBrowserLiveView> {
    const view = await fleetService.getBrowserLiveView(runId)
    browserAutomation.value = view.automation ?? null
    browserTakenBy.value = view.takenBy ?? null
    return view
  }

  /**
   * Takes the run's browser from the agent (`take`) or gives it back. A give-back the fleet refuses
   * because the pause has not landed yet becomes a notice in the panel rather than an error toast.
   */
  async function setBrowserControl(runId: string, take: boolean) {
    browserControlPending.value = true
    browserControlNotice.value = null
    error.value = null
    try {
      const result = await fleetService.setBrowserControl(runId, take)
      browserAutomation.value = result.automation
      await fetchRun(runId, { background: true })
    } catch (e) {
      if (!take && getErrorStatus(e) === BROWSER_STILL_PAUSING_STATUS) {
        browserControlNotice.value = fleetErrorText(e, 'The run is still pausing. Give the browser back in a few seconds.')
        return
      }
      error.value = fleetErrorText(e, take ? 'Failed to take over the browser' : 'Failed to give the browser back')
      throw e
    } finally {
      browserControlPending.value = false
    }
  }

  function clearBrowserState() {
    browserAutomation.value = null
    browserTakenBy.value = null
    browserControlPending.value = false
    browserControlNotice.value = null
  }

  async function runCommand(runId: string, command: () => Promise<unknown>, failure: string) {
    runCommandPending.value = true
    error.value = null
    try {
      await command()
      await fetchRun(runId, { background: true })
    } catch (e) {
      error.value = fleetErrorText(e, failure)
      throw e
    } finally {
      runCommandPending.value = false
    }
  }

  async function sayToRun(runId: string, text: string) {
    await runCommand(runId, () => fleetService.sayToRun(runId, text), 'Failed to send the message')
  }

  async function answerRun(runId: string, askId: string, text: string) {
    await runCommand(runId, () => fleetService.answerRun(runId, askId, text), 'Failed to send the answer')
  }

  async function pauseRun(runId: string) {
    await runCommand(runId, () => fleetService.pauseRun(runId), 'Failed to pause the run')
  }

  /** Ends a running or paused run for good; the runner records who stopped it. */
  async function stopRun(runId: string) {
    await runCommand(runId, () => fleetService.stopRun(runId), 'Failed to stop the run')
  }

  /** Asks the fleet for a plain-language cause, which lands on the run record shortly after. */
  async function explainRun(runId: string) {
    await runCommand(runId, () => fleetService.explainRun(runId), 'Failed to ask for an explanation')
  }

  /** Turns this person's Slack DM for when the run finishes on or off. */
  async function setRunNotify(runId: string, on: boolean) {
    await runCommand(
      runId,
      async () => {
        const result = await fleetService.setRunNotify(runId, on)
        notifyRequested.value = { ...notifyRequested.value, [runId]: result.notify }
      },
      on ? 'Failed to turn on the notification' : 'Failed to turn off the notification'
    )
  }

  /** The viewer's own 👍 / 👎 on a run (null takes it away); the drawer and the list show the new tally at once. */
  async function gradeRun(runId: string, grade: FleetRunGradeValue | null, why?: string) {
    error.value = null
    try {
      const { grades } = await fleetService.gradeRun(runId, grade, why)
      if (selectedRun.value?.runId === runId) selectedRun.value = { ...selectedRun.value, grades }
      runs.value = runs.value.map(run => (run.runId === runId ? { ...run, grades } : run))
    } catch (e) {
      error.value = fleetErrorText(e, 'Failed to save your grade')
      throw e
    }
  }

  /**
   * Resumes a paused run. A run with a browser resumes through the give-back, which hands the browser back
   * to the agent first, so a run any operator took over never resumes without its browser; any message
   * follows once the resumed session has started. A session that has ended has nothing to hand back, so
   * that run resumes plainly, carrying the message.
   */
  async function resumeRun(runId: string, text?: string, options: { browser?: boolean } = {}) {
    if (!options.browser) {
      await runCommand(runId, () => fleetService.resumeRun(runId, text), 'Failed to resume the run')
      return
    }
    const message = text?.trim() ? text : undefined
    let tailBefore: string[] = []
    let givenBack = false
    await runCommand(
      runId,
      async () => {
        if (message) tailBefore = (await fleetService.getRun(runId)).live?.tail ?? []
        try {
          browserAutomation.value = (await fleetService.setBrowserControl(runId, false)).automation
          givenBack = true
        } catch (e) {
          if (!isBrowserSessionGone(e)) throw e
          await fleetService.resumeRun(runId, message)
        }
      },
      'Failed to resume the run'
    )
    if (!message || !givenBack) return
    await runCommand(
      runId,
      async () => {
        await waitForResumedSession(runId, tailBefore)
        await fleetService.sayToRun(runId, message)
      },
      'The run resumed, but the message was not sent. Send it again.'
    )
  }

  /**
   * A paused run refuses messages, and a resumed run clears its inbox while it starts, so a message sent
   * before its new session starts is lost. Waits for that session's start line in the run's log.
   */
  async function waitForResumedSession(runId: string, tailBefore: string[]) {
    const deadline = Date.now() + RESUMED_SESSION_WAIT_MS
    for (;;) {
      const tail = (await fleetService.getRun(runId)).live?.tail ?? []
      if (linesAfter(tailBefore, tail).some(line => line.includes(RESUMED_SESSION_STARTED))) return
      if (Date.now() >= deadline) {
        throw new Error('The run resumed, but its session did not start in time, so the message was not sent. Send it again.')
      }
      await new Promise(resolve => setTimeout(resolve, RESUMED_SESSION_POLL_MS))
    }
  }

  async function startRun(request: FleetStartRunRequest): Promise<FleetStartRunResult> {
    error.value = null
    try {
      const result = await fleetService.startRun(request)
      await fetchRuns()
      return result
    } catch (e) {
      error.value = fleetErrorText(e, 'Failed to start the run')
      throw e
    }
  }

  async function updateLoopState(name: string, state: FleetLoopState) {
    loopsUpdating.value = { ...loopsUpdating.value, [name]: true }
    error.value = null
    try {
      await fleetService.setLoopState(name, state)
      loops.value = loops.value.map(loop => (loop.name === name ? { ...loop, state } : loop))
    } catch (e) {
      error.value = fleetErrorText(e, 'Failed to change the loop')
      throw e
    } finally {
      loopsUpdating.value = { ...loopsUpdating.value, [name]: false }
    }
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    activeRunsRequestId++
    summary.value = null
    summaryLoading.value = false
    loops.value = []
    loopsLoading.value = false
    loopsUpdating.value = {}
    runs.value = []
    fleetRunningRuns.value = null
    runsLoading.value = false
    recentFailedRuns.value = []
    recentFailedMore.value = false
    recentFailedUnread.value = false
    liveSteps.value = {}
    clearFilters()
    clearSelectedRun()
    runCommandPending.value = false
    notifyRequested.value = {}
    audit.value = []
    auditLoading.value = false
    config.value = {}
    ceilings.value = {}
    describe.value = {}
    kinds.value = {}
    configLoading.value = false
    configSaving.value = {}
    configErrors.value = {}
    configReadOnlyReason.value = null
    error.value = null
    accessDenied.value = false
    accessDeniedText.value = null
    unreachable.value = false
    failedSections.value = noFailedSections()
    sectionFailureTexts.value = {}
    lastRefreshedAt.value = null
  }

  return {
    summary,
    summaryLoading,
    loops,
    loopsLoading,
    loopsUpdating,
    runs,
    runsLoading,
    activityFilter,
    runFilters,
    liveSteps,
    pageCursors,
    nextCursor,
    recentFailedRuns,
    recentFailedMore,
    recentFailedUnread,
    selectedRun,
    selectedRunLoading,
    runCommandPending,
    notifyRequested,
    browserAutomation,
    browserTakenBy,
    browserControlPending,
    browserControlNotice,
    audit,
    auditLoading,
    config,
    ceilings,
    describe,
    kinds,
    operators,
    configLoading,
    configSaving,
    configErrors,
    configReadOnlyReason,
    isConfigReadOnly,
    canAct,
    configUnreadReason,
    error,
    failedSections,
    sectionFailureTexts,
    accessDenied,
    accessDeniedText,
    unreachable,
    isConfigured,
    hasError,
    isLoading,
    hasOlderRuns,
    hasNewerRuns,
    runsPageNumber,
    visibleRuns,
    fleetRunningRuns,
    runningRuns,
    bookkeepingHidden,
    showSkipped,
    setShowSkipped,
    hiddenSkippedCount,
    skippedCount,
    setMineSets,
    knownActivities,
    fetchSummary,
    fetchLoops,
    fetchRuns,
    fetchOlderRuns,
    fetchNewerRuns,
    setActivityFilter,
    setRunFilters,
    clearFilters,
    fetchLiveSteps,
    fetchAudit,
    fetchOverview,
    lastRefreshedAt,
    fetchRecentFailures,
    fetchConfig,
    saveConfig,
    fetchRun,
    clearSelectedRun,
    fetchBrowserLiveView,
    setBrowserControl,
    clearBrowserState,
    sayToRun,
    answerRun,
    pauseRun,
    stopRun,
    explainRun,
    setRunNotify,
    gradeRun,
    resumeRun,
    startRun,
    updateLoopState,
    clearError,
    reset
  }
})

function stripLive(run: FleetRunDetail): FleetRun {
  const { live: _live, ...record } = run
  return record
}

/** The fleet answers a save with the saved record; `running` is kept from the list when it is left out. */
function mergeSavedRecord(listed: FleetConfigRecord | undefined, saved: FleetConfigRecord): FleetConfigRecord {
  return { ...listed, ...saved, running: saved.running ?? listed?.running ?? true }
}

/** What a failed fleet call says on the page: the fleet's own message (a 403 says why), else the usual. */
export function fleetErrorText(failure: unknown, fallback: string): string {
  return fleetOwnErrorText(failure) ?? getErrorMessage(failure, fallback)
}

/**
 * What a section says when the fleet refused to show it to this sign-in (403), with the fleet's own reason; null for
 * any other failure. A refusal is a permission, never "not available on this fleet yet".
 */
export function refusedReadText(failure: unknown, what: string): string | null {
  if (getErrorStatus(failure) !== 403) return null
  const reason = fleetOwnErrorText(failure)
  return `${what} is not shown to this sign-in${reason ? `: ${reason}` : ''}. A fleet operator can see it.`
}

/**
 * What a section's error panel says about a failed read: the fleet answered with an error, refused,
 * or did not answer at all, which are different things for the person to do about.
 */
export function sectionFailureText(failure: unknown): string {
  const status = getErrorStatus(failure)
  if (status === undefined) return 'The fleet API did not answer. Check your connection or VPN and try again.'
  if (status >= 500) return `The fleet API had an error (${status}). Try again in a minute.`
  const reason = fleetOwnErrorText(failure)
  return reason ? `The fleet API refused this (${status}): ${reason}` : `The fleet API refused this (${status}).`
}

/** A give-back refused because the run has no live browser session (404, or 409 "has ended"); a run still pausing is not. */
function isBrowserSessionGone(failure: unknown): boolean {
  const status = getErrorStatus(failure)
  return status === 404 || (status === 409 && Boolean(fleetOwnErrorText(failure)?.includes(BROWSER_SESSION_ENDED_TEXT)))
}

/** The lines a run's log gained since `before`; the log is a sliding window of an append-only file. */
function linesAfter(before: string[], after: string[]): string[] {
  if (!before.length) return after
  const last = after.lastIndexOf(before[before.length - 1]!)
  return last === -1 ? after : after.slice(last + 1)
}

/** The fleet's own `{error}` text, which `getErrorMessage` deliberately blanks for a 403. */
function fleetOwnErrorText(failure: unknown): string | null {
  if (!isApiError(failure)) return null
  const text = failure.response?.data?.error
  return typeof text === 'string' && text.trim() ? text : null
}
